import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';
import CommerceForm from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.tsx';
import type { CommerceFormSubmitPayload } from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceFormTypes.ts';
import CommerceSheetJobLoader, {
  COMMERCE_BUSY_PHASE,
} from '../../components/Comercios/CommerceSheetJobLoader/CommerceSheetJobLoader.tsx';
import { buildCommercePrefillFromUserData } from '../../utils/commercePrefill.ts';
import { createCommerce } from '../../services/commerceService.ts';
import { extractCommerceIdFromCreateResponse } from '../../services/materialCommerceSheetService.ts';
import { loadUserData } from '../../store/actions';
import { openMainModal } from '../../store/actions/profileActions';
import { buildProfileUrl, getUserDisplayName } from '../../utils/navigationHelpers.ts';
import { userHasCommerceRegistration } from '../../utils/userCommerces.ts';
import styles from './CommerceAltaPage.module.scss';

const CommerceAltaPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const userData = useSelector((s: { general?: { userData?: Record<string, unknown> } }) => s.general?.userData);
  const [busy, setBusy] = useState(false);

  const userId = userData?.id != null ? Number(userData.id) : 0;
  const prefill = useMemo(() => buildCommercePrefillFromUserData(userData), [userData]);
  const flexibleSheetEnabled = userData?.type !== 'JOBBOARD';

  useEffect(() => {
    if (userData == null || !userHasCommerceRegistration(userData)) return;
    navigate('/comercios/lista-precios', { replace: true });
  }, [userData, navigate]);

  const handleSubmit = useCallback(
    async (payload: CommerceFormSubmitPayload) => {
      setBusy(true);
      try {
        const createRes = await createCommerce({
          name: payload.name,
          description: payload.description,
          zone: payload.zone,
          delivery: payload.delivery,
          delivery_condition: payload.delivery_condition,
          url_site_web: payload.url_site_web,
          jurisdiccionId: payload.jurisdiccionId,
          userId: payload.userId,
          logo: payload.logo,
          deliveryScope: payload.deliveryScope,
          deliveryRadiusKm:
            payload.deliveryScope === 'LOCAL' ? payload.deliveryRadiusKm : undefined,
          latitude: payload.deliveryScope === 'LOCAL' ? payload.latitude : undefined,
          longitude: payload.deliveryScope === 'LOCAL' ? payload.longitude : undefined,
          listedZonesByJurisdiction:
            payload.deliveryScope === 'LISTED_ZONES' && payload.listedZonesByJurisdiction?.length
              ? payload.listedZonesByJurisdiction
              : undefined,
          listedGeorefZonaIds:
            payload.deliveryScope === 'LISTED_ZONES' &&
            (!payload.listedZonesByJurisdiction || payload.listedZonesByJurisdiction.length === 0)
              ? payload.listedGeorefZonaIds
              : undefined,
        });
        const commerceId = extractCommerceIdFromCreateResponse(createRes);
        dispatch(loadUserData());
        const nameDisplay = getUserDisplayName(userData);
        if (commerceId != null) {
          if (flexibleSheetEnabled) {
            navigate(`/comercios/lista-precios?commerceId=${commerceId}&desdeAlta=1`);
          } else {
            navigate(`/comercios/catalogo?commerceId=${commerceId}&desdeAlta=1`);
          }
          return;
        }
        navigate(buildProfileUrl(String(userId), nameDisplay, 'Resumen'));
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No pudimos registrar el comercio. Intentá de nuevo.';
        dispatch(
          openMainModal({
            title: 'Registro de comercio',
            body: msg,
            iconType: 'error',
            isSuccess: true,
            confirmHandler: null,
          })
        );
      } finally {
        setBusy(false);
      }
    },
    [dispatch, flexibleSheetEnabled, navigate, userData, userId]
  );

  const handleSkip = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (!userId) {
    return null;
  }

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbBar}>
          <Breadcrumbs />
        </div>
        <main className={styles.main}>
          {busy ? (
            <div className={styles.mainReplace}>
              <CommerceSheetJobLoader
                phaseCode={COMMERCE_BUSY_PHASE.create}
                fileLabel={null}
                progressDetail=""
              />
            </div>
          ) : (
            <div className={styles.commerceStack}>
              <div className={styles.commerceHero}>
                <div className={styles.commerceHeroIntro}>
                  <h1 className={styles.commerceHeroTitle}>Registrar comercio</h1>
                  <div className={styles.sheetIntro}>
                    <p className={styles.subtitle}>
                      Completá los datos del comercio y la zona de alcance; cuando guardes vas a poder cargar tus
                      precios y productos desde la pantalla de catálogo o con un archivo cuando corresponda a tu cuenta.
                    </p>
                    <ul className={styles.sheetValueList}>
                      <li>La ficha del comercio queda lista en Edify apenas confirmás estos datos.</li>
                      <li>Sumar materiales o planilla es un paso aparte para orientarte mejor y poder salir antes si hace falta.</li>
                      <li>Cuentas cliente: después te llevamos a cargar lista por archivo; otras cuentas: al catálogo para dar de alta de a uno.</li>
                      <li>Pasos posteriores y el perfil dejan avisarte si algo quedó pendiente.</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className={styles.commerceMain}>
                <CommerceForm
                  prefill={prefill}
                  userId={userId}
                  disabled={false}
                  cancelLabel="Cancelar"
                  flexibleSheetEnabled={flexibleSheetEnabled}
                  suppressCatalogBlocks={true}
                  onSkip={handleSkip}
                  onSubmit={handleSubmit}
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CommerceAltaPage;
